import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useBlueMonk } from '@/contexts/BlueMonkContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, Clock, CheckCircle2, FileText, 
  User, LogOut, RefreshCw
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { API } from '@/constants/bluemonk';

// Import modular components
import {
  StatCard,
  ActionRequiredTab,
  ApprovalsTab,
  AssetLibraryTab,
  CommentsTab,
  ProgressTab,
  SubmitModal,
  ApprovalModal,
  RevisionModal,
} from '@/components/bluemonk';

const BlueMonkPortal = () => {
  const navigate = useNavigate();
  const { 
    clientIdentity, 
    isClientPinVerified,
    selectIdentity,
    switchIdentity, 
    clientLogout,
    getActorInfo,
    getClientAuthHeader,
    clientTeam
  } = useBlueMonk();

  const [activeTab, setActiveTab] = useState('action-required');
  const [stats, setStats] = useState({ overdueInputs: 0, pendingInputs: 0, awaitingApproval: 0, completed: 0 });
  const [inputRequests, setInputRequests] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [comments, setComments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [showIdentitySelect, setShowIdentitySelect] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitializing(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (initializing) return;
    
    if (!isClientPinVerified()) {
      navigate('/bluemonk');
      return;
    }
    if (!clientIdentity) {
      setShowIdentitySelect(true);
    } else {
      setShowIdentitySelect(false);
    }
  }, [isClientPinVerified, clientIdentity, navigate, initializing]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getClientAuthHeader();
      
      const [statsRes, inputsRes, delivRes, commentsRes, progressRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/input-requests`, { headers }),
        axios.get(`${API}/deliverables`, { headers }),
        axios.get(`${API}/comments`, { headers }),
        axios.get(`${API}/progress`, { headers }),
      ]);

      setStats(statsRes.data);
      setInputRequests(inputsRes.data);
      setDeliverables(delivRes.data);
      setComments(commentsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        clientLogout();
        navigate('/bluemonk');
      }
    } finally {
      setLoading(false);
    }
  }, [getClientAuthHeader, clientLogout, navigate]);

  useEffect(() => {
    if (clientIdentity) {
      fetchData();
    }
  }, [clientIdentity, fetchData]);

  const handleLogout = () => {
    clientLogout();
    navigate('/bluemonk');
  };

  const handleSwitchPerson = () => {
    switchIdentity();
    setShowIdentitySelect(true);
  };

  const handleIdentitySelect = (person) => {
    selectIdentity(person);
    setShowIdentitySelect(false);
  };

  // Filter functions
  const filterByBrand = (items) => {
    if (brandFilter === 'all') return items;
    return items.filter(item => item.brand === brandFilter || item.brand === 'all');
  };

  const filterByStatus = (items) => {
    if (statusFilter === 'all') return items;
    return items.filter(item => item.status === statusFilter);
  };

  const today = new Date().toISOString().split('T')[0];
  const overdueInputs = inputRequests.filter(i => i.status === 'pending' && i.deadline < today);

  // Identity Selection Screen
  if (showIdentitySelect || !clientIdentity) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-lg"
        >
          <Card className="p-8 bg-white shadow-xl">
            <div className="text-center mb-8">
              <div className="bg-white rounded-xl p-4 mx-auto mb-6 inline-block shadow-sm border">
                <img 
                  src="/logo.png" 
                  alt="Content Vista" 
                  className="h-10 w-auto"
                />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Who are you?</h1>
              <p className="text-slate-500">Select your identity to continue</p>
            </div>

            <div className="space-y-3">
              {clientTeam.map((person) => (
                <button
                  key={person.id}
                  onClick={() => handleIdentitySelect(person)}
                  className="w-full flex items-center gap-4 p-4 bg-slate-50 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-left"
                  data-testid={`identity-btn-${person.id}`}
                >
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{person.name}</p>
                    <p className="text-sm text-slate-500">{person.role}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-xs text-slate-400">
                Your actions will be logged under this identity
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img 
                src="/logo.png" 
                alt="Content Vista" 
                className="h-8 w-auto"
              />
              <div className="h-6 w-px bg-slate-200" />
              <span className="font-semibold text-slate-700">BlueMonk Ventures</span>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  Acting as: {clientIdentity.name}
                </span>
                <Badge variant="outline" className="text-xs">
                  {clientIdentity.role}
                </Badge>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSwitchPerson}
                data-testid="switch-person-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Switch Person
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Exit
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <StatCard 
            icon={AlertCircle} 
            label="Overdue Inputs" 
            value={stats.overdueInputs}
            color="red"
            onClick={() => { setActiveTab('action-required'); setStatusFilter('pending'); }}
          />
          <StatCard 
            icon={Clock} 
            label="Pending Inputs" 
            value={stats.pendingInputs}
            color="amber"
            onClick={() => { setActiveTab('action-required'); setStatusFilter('pending'); }}
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Inputs Provided" 
            value={stats.submittedInputs || 0}
            color="green"
            onClick={() => { setActiveTab('action-required'); setStatusFilter('submitted'); }}
          />
          <StatCard 
            icon={FileText} 
            label="Awaiting Approval" 
            value={stats.awaitingApproval}
            color="blue"
            onClick={() => setActiveTab('approvals')}
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Approved" 
            value={stats.completed}
            color="teal"
            onClick={() => setActiveTab('approvals')}
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="action-required" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Action Required
              {overdueInputs.length > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  {overdueInputs.length} overdue
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approvals" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Approvals
            </TabsTrigger>
            <TabsTrigger value="asset-library" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Asset Library
            </TabsTrigger>
            <TabsTrigger value="comments" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Comments & Feedback
            </TabsTrigger>
            {/* Progress tab hidden for now - will enable later */}
            {/* <TabsTrigger value="progress" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Progress
            </TabsTrigger> */}
          </TabsList>

          {/* Filter Bar */}
          <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Brand:</Label>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  <SelectItem value="renew-circuits">Renew Circuits</SelectItem>
                  <SelectItem value="bluemonk-group">BlueMonk Group</SelectItem>
                  <SelectItem value="deshwal">Deshwal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activeTab === 'action-required' && (
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Status:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="needs-revision">Needs Revision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Tab Content */}
          <TabsContent value="action-required">
            <ActionRequiredTab 
              items={filterByStatus(filterByBrand(inputRequests))}
              today={today}
              onSubmit={(item) => { setSelectedItem(item); setSubmitModalOpen(true); }}
            />
          </TabsContent>

          <TabsContent value="approvals">
            <ApprovalsTab 
              items={filterByBrand(deliverables)}
              onApprove={(item) => { setSelectedItem(item); setApprovalModalOpen(true); }}
              onRequestRevision={(item) => { setSelectedItem(item); setRevisionModalOpen(true); }}
              onComment={fetchData}
              actor={getActorInfo()}
              headers={getClientAuthHeader()}
            />
          </TabsContent>

          <TabsContent value="asset-library">
            <AssetLibraryTab 
              inputRequests={filterByBrand(inputRequests)}
            />
          </TabsContent>

          <TabsContent value="comments">
            <CommentsTab 
              comments={filterByBrand(comments)}
              onNavigate={(itemId, itemType) => {
                if (itemType === 'input-request') {
                  setActiveTab('action-required');
                } else {
                  setActiveTab('approvals');
                }
              }}
            />
          </TabsContent>

          <TabsContent value="progress">
            <ProgressTab items={filterByBrand(progress)} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <SubmitModal 
        open={submitModalOpen}
        onOpenChange={setSubmitModalOpen}
        item={selectedItem}
        actor={getActorInfo()}
        headers={getClientAuthHeader()}
        onSuccess={() => { fetchData(); setSubmitModalOpen(false); }}
      />

      <ApprovalModal 
        open={approvalModalOpen}
        onOpenChange={setApprovalModalOpen}
        item={selectedItem}
        actor={getActorInfo()}
        headers={getClientAuthHeader()}
        onSuccess={() => { fetchData(); setApprovalModalOpen(false); }}
      />

      <RevisionModal 
        open={revisionModalOpen}
        onOpenChange={setRevisionModalOpen}
        item={selectedItem}
        actor={getActorInfo()}
        headers={getClientAuthHeader()}
        onSuccess={() => { fetchData(); setRevisionModalOpen(false); }}
      />
    </div>
  );
};

export default BlueMonkPortal;
